import { useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { useTheme } from "next-themes"
import {
  ArrowRight,
  BarChart3,
  Bot,
  Building2,
  Calendar,
  Check,
  ChevronDown,
  Clock,
  CreditCard,
  Dumbbell,
  Globe,
  Headphones,
  Instagram,
  MapPin,
  Megaphone,
  Minus,
  Plus,
  Salad,
  ScanLine,
  ShieldCheck,
  ShoppingBag,
  Smartphone,
  Sparkles,
  Star,
  Users,
} from "lucide-react"
import { Reveal } from "@/components/shared/reveal"
import { GrantFooterBand } from "@/components/shared/grant-attribution"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { SEOHead } from "@/components/seo-head"

// ─── i18n ────────────────────────────────────────────────────────────────────

type Lang = "en" | "sq" | "it"
type ModuleKey = "business" | "classes" | "workout" | "diet" | "store" | "whitelabel" | "marketing"

const LANGS: { code: Lang; label: string }[] = [
  { code: "en", label: "English" },
  { code: "sq", label: "Shqip" },
  { code: "it", label: "Italiano" },
]

interface T {
  announcement: { eyebrow: string; message: string; cta: string }
  nav: { signIn: string; getStarted: string; features: string; showcase: string; pricing: string; customers: string }
  hero: {
    badge: string; heading1: string; heading2: string
    description: string; cta1: string; cta2: string
    footnote: string
    logoBar: string
    offerPill: string
  }
  stats: { gyms: string; activeMembers: string; uptimeSla: string; support: string }
  features: {
    eyebrow: string; heading: string; subheading: string; valueProp: string
    cards: { title: string; desc_short: string; desc: string }[]
  }
  showcase: {
    eyebrow: string; heading: string; subheading: string
    tabs: { diet: string; workout: string; promo: string }
    comingSoon: string
    dietDesc: string; workoutDesc: string; promoDesc: string
  }
  mobile: { eyebrow: string; heading: string; sub: string; bullet1: string; bullet2: string; bullet3: string }
  benefits: {
    eyebrow: string
    heading: string  // contains "NOT" as a sentinel — the BrandMark renders in its place
    subheading: string
    items: { title: string; desc: string }[]
  }
  pricing: {
    eyebrow: string; heading: string; subheading: string
    comingSoon: string; soon: string; perMonth: string; perMonthEach: string
    modules: Record<ModuleKey, { name: string; features: string[] }>
    extraLocations: { name: string; features: string[] }
    calculator: {
      title: string; selectAll: string; extraLocations: string
      modules: (n: number) => string; extraLocs: (n: number) => string; total: string
    }
    cta: { sub: string; btn1: string; btn2: string }
    offer: {
      eyebrow: string; heading: string; subheading: string
      unit: string; compare: string; cta: string
    }
  }
  caseStudy: {
    eyebrow: string; heading1: string; heading2: string; description: string
    gyms: { subtitle: string; members: string; highlight: string; features: string[] }[]
    followInstagram: string; testimonial: string; operator: string; statsLabels: string[]
  }
  cta: {
    heading1: string; heading2: string; description: string
    btn1: string; btn2: string; check1: string; check2: string; check3: string
  }
  footer: {
    tagline: string
    productHeading: string; productLinks: { label: string; href: string }[]
    legalHeading: string; legalLinks: { label: string; href: string }[]
    copyright: string
  }
}

const translations: Record<Lang, T> = {
  // ── English ────────────────────────────────────────────────────────────────
  en: {
    announcement: { eyebrow: "Limited time", message: "€499/year per location · Up to 90% off monthly pricing", cta: "See offer" },
    nav: { signIn: "Sign in", getStarted: "Get started", features: "Features", showcase: "Showcase", pricing: "Pricing", customers: "Customers" },
    hero: {
      badge: "AI-powered fitness platform",
      heading1: "Run your fitness business on",
      heading2: "intelligent automation.",
      description: "The platform powering modern gym chains and franchises. Automate operations, personalize member experiences, and grow with AI built for fitness.",
      cta1: "Start free trial",
      cta2: "Schedule a demo",
      footnote: "No credit card · 14-day trial · Cancel anytime",
      logoBar: "Trusted by leading fitness operators",
      offerPill: "Limited-time launch offer · €499/year per location",
    },
    stats: { gyms: "Gyms", activeMembers: "Active members", uptimeSla: "Uptime SLA", support: "Support" },
    features: {
      eyebrow: "Platform",
      heading: "Everything to run a modern gym",
      subheading: "Modular tools that automate, optimise, and personalise every part of your fitness business.",
      valueProp: "AI does the work. You run the gym.",
      cards: [
        { title: "White-label multi-tenancy", desc_short: "Built for chains and franchises", desc: "Manage unlimited locations from one platform. Performance trends per site; each gym keeps its own brand and independence." },
        { title: "AI promo builder",          desc_short: "Boost revenue with smart promos", desc: "AI designs targeted promotions, finds the best timing, and personalises discounts to maximise conversions." },
        { title: "AI class scheduling",       desc_short: "Optimised booking management",   desc: "AI analyses attendance and demand to suggest optimal schedules, auto-fill waitlists, and balance trainer workloads." },
        { title: "Payment processing",        desc_short: "Automated revenue management",   desc: "Recurring billing, payment plans, invoicing, and AI-generated forecasts. Accept all major payment methods." },
        { title: "Predictive analytics",      desc_short: "Business intelligence, live",    desc: "AI surfaces revenue trends, predicts peak hours, identifies at-risk members, and recommends actions in real time." },
        { title: "AI nutrition & training",   desc_short: "Personal plans for every member", desc: "AI builds personalised diet and workout plans from goals, body metrics, and progress — and adapts them automatically." },
      ],
    },
    showcase: {
      eyebrow: "Showcase",
      heading: "See AI in action",
      subheading: "Each gym gets a fully branded experience. Here is how the same AI tools look across different fitness businesses on our platform.",
      tabs: { diet: "AI Diet Planner", workout: "AI Workout Builder", promo: "AI Promo Builder" },
      comingSoon: "COMING SOON",
      dietDesc: "AI generates personalised meal plans with macro tracking, food databases, and automatic adjustments — branded to each gym.",
      workoutDesc: "Smart programs built around fitness level, recovery data, and performance trends. Plans evolve as members progress.",
      promoDesc: "AI-powered promotions that target the right members at the right time to maximise revenue and retention.",
    },
    mobile: {
      eyebrow: "Mobile",
      heading: "Native iOS & Android apps for every gym",
      sub: "Members get a fully branded mobile experience. You stay in control of every release.",
      bullet1: "Custom branding — logo, colors, app icon",
      bullet2: "QR check-in, bookings, plans, and store",
      bullet3: "Push notifications and in-app messages",
    },
    benefits: {
      eyebrow: "Why us",
      heading: "Why THE NOT FIT?",
      subheading: "The most advanced AI-powered fitness management platform, trusted by industry leaders.",
      items: [
        { title: "AI-Powered Diet Management", desc: "Generate personalised nutrition plans in seconds. AI adapts macros and meal schedules based on each member's goals, allergies, and body metrics." },
        { title: "AI Workout Plan Builder",    desc: "Smart training programs built around fitness level, recovery data, and performance trends. Plans evolve automatically as members progress." },
        { title: "Verified Access Control",    desc: "AI-driven identity checks via QR code, NFC card, PIN, or biometric scan. Anomaly detection flags suspicious entries across all locations in real time." },
        { title: "Progress Insights",           desc: "AI analyses body composition, strength records, and cardio benchmarks to surface trends and predict plateaus — giving trainers and members clear next steps." },
        { title: "Retention Engine",            desc: "Machine-learning models predict churn before it happens. AI triggers personalised re-engagement offers, streak rewards, and win-back campaigns automatically." },
        { title: "Operations Dashboard",        desc: "AI forecasts floor occupancy, predicts class demand, optimises trainer schedules, and highlights revenue opportunities — live across every location." },
      ],
    },
    pricing: {
      eyebrow: "Pricing",
      heading: "Modular and transparent",
      subheading: "Pay only for the modules you need. Add or remove features as your business grows.",
      comingSoon: "COMING SOON", soon: "SOON", perMonth: "/month", perMonthEach: "/month each",
      modules: {
        business:   { name: "Business Management", features: ["Member Check-Ins (QR, Card, PIN)", "Multi-Location Dashboard", "Employee Management", "Reports & Analytics"] },
        classes:    { name: "Classes",             features: ["Scheduling (recurring & one-off)", "Self-service bookings & waitlists", "Capacity & trainer assignment", "Automated notifications"] },
        workout:    { name: "Workout & Training",  features: ["Workout Plan Creator", "Workout Logger", "Exercise Library", "Performance Reporting"] },
        diet:       { name: "Diet Planner",        features: ["Personalized meal plans", "Food database (local foods)", "Calorie & macro tracking", "Workout plan integration"] },
        store:      { name: "Store",               features: ["Product catalog & categories", "Order management & tracking", "Configurable order workflows", "Food, supplements, merch & more"] },
        whitelabel: { name: "White Label",         features: ["Personal iOS & Android apps", "Personalized website", "Custom domain", "Full brand customization"] },
        marketing:  { name: "Marketing & CRM",     features: ["CRM with member profiles", "Automated renewal reminders", "Email, SMS, Push & WhatsApp", "Onboarding automation"] },
      },
      extraLocations: { name: "Extra Locations", features: ["Full module access per location", "Separate management & reports", "Custom branding per gym", "Independent operations"] },
      calculator: {
        title: "Pricing calculator", selectAll: "Select all", extraLocations: "Extra locations",
        modules: (n) => `${n} module${n !== 1 ? "s" : ""}`,
        extraLocs: (n) => `+${n} extra location${n !== 1 ? "s" : ""}`,
        total: "Total",
      },
      cta: { sub: "Start with the essentials and add modules as you grow.", btn1: "Get started", btn2: "Talk to sales" },
      offer: {
        eyebrow: "Limited-time launch deal",
        heading: "All-in annual location pricing",
        subheading: "Lock in €499/year per gym location — every module, every member, every device. A massive discount over our monthly modular plans, available for a very limited time as we open up multi-location partnerships.",
        unit: "/year per location",
        compare: "Up to 90% off standard monthly pricing · All modules included",
        cta: "Claim offer",
      },
    },
    caseStudy: {
      eyebrow: "Case study", heading1: "Powering the", heading2: "Flex Gym Group",
      description: "3,000+ members across multiple locations — managed seamlessly on THE NOT FIT. From premium gyms to CrossFit studios to in-house restaurants, one system runs it all.",
      gyms: [
        { subtitle: "Premium gym in the capital",     members: "2,000+", highlight: "Members", features: ["Full equipment floor", "Group classes", "Personal training", "Sauna & recovery"] },
        { subtitle: "Premium seaside gym",            members: "1,500+", highlight: "Members", features: ["Seaside training", "Outdoor areas", "Seasonal programs", "Resort integration"] },
        { subtitle: "CrossFit, pilates & restaurant", members: "1,200+", highlight: "Members", features: ["CrossFit box", "Pilates studio", "In-house restaurant", "Sports nutrition bar"] },
      ],
      followInstagram: "Follow on Instagram",
      testimonial: "THE NOT FIT lets us manage 3 completely different gym concepts — from a premium fitness center to a CrossFit box with a restaurant — all under one platform. Our members love the seamless experience.",
      operator: "Multi-location fitness operator, Albania",
      statsLabels: ["Locations", "Total members", "Classes / week", "Platform"],
    },
    cta: {
      heading1: "Ready to scale your", heading2: "fitness business with AI?",
      description: "Join the growing number of fitness businesses using AI-powered tools from THE NOT FIT to automate and grow.\nStart your free 14-day trial today — no credit card required.",
      btn1: "Start free trial", btn2: "Talk to sales",
      check1: "14-day free trial", check2: "No credit card", check3: "Cancel anytime",
    },
    footer: {
      tagline: "AI-powered fitness management platform for gyms, studios, and fitness businesses.",
      productHeading: "Product",
      productLinks: [
        { label: "Features", href: "#features" },
        { label: "Showcase", href: "#showcase" },
        { label: "Pricing",  href: "#pricing" },
        { label: "Customers", href: "#customers" },
      ],
      legalHeading: "Legal",
      legalLinks: [
        { label: "Privacy Policy",   href: "/privacy" },
        { label: "Terms of Service", href: "/privacy" },
        { label: "Data Deletion",    href: "/data-deletion" },
        { label: "Support",          href: "/support" },
      ],
      copyright: "© 2026 THE NOT FIT. All rights reserved.",
    },
  },

  // ── Albanian / Shqip ───────────────────────────────────────────────────────
  sq: {
    announcement: { eyebrow: "Kohë e kufizuar", message: "50,000 ALL/vit për vendndodhje · Deri në 90% zbritje nga çmimi mujor", cta: "Shiko ofertën" },
    nav: { signIn: "Hyr", getStarted: "Fillo Tani", features: "Veçoritë", showcase: "Demo", pricing: "Çmimet", customers: "Klientët" },
    hero: {
      badge: "Platformë fitness me AI",
      heading1: "Drejto biznesin tënd fitness me",
      heading2: "automatizim inteligjent.",
      description: "Platforma që fuqizon palestrat dhe franshizat moderne. Automatizo operacionet, personalizo përvojën e anëtarëve dhe rrit biznesin me AI.",
      cta1: "Fillo provën falas",
      cta2: "Rezervo një demo",
      footnote: "Pa kartë krediti · Provë 14-ditore · Anulo në çdo kohë",
      logoBar: "Të besuar nga operatorët kryesorë të fitnesit",
      offerPill: "Ofertë me kohë të kufizuar · 50,000 ALL/vit për vendndodhje",
    },
    stats: { gyms: "Palestra", activeMembers: "Anëtarë aktivë", uptimeSla: "Uptime SLA", support: "Mbështetje" },
    features: {
      eyebrow: "Platforma",
      heading: "Gjithçka për të drejtuar një palestër moderne",
      subheading: "Mjete modulare që automatizojnë, optimizojnë dhe personalizojnë çdo aspekt të biznesit fitness.",
      valueProp: "AI bën punën. Ti drejton palestrën.",
      cards: [
        { title: "Multi-tenant white-label",     desc_short: "Ndërtuar për franshiza dhe zinxhirë",    desc: "Menaxho vendndodhje të pakufizuara nga një platformë. Trende performancës për çdo vend, çdo palestrë me markën e saj." },
        { title: "Krijues promosh me AI",        desc_short: "Rrit të ardhurat me promo AI",           desc: "AI dizajnon promocione të targetuara, gjen kohën më të mirë dhe personalizon zbritjet për maksimum konvertimesh." },
        { title: "Planifikim klasash me AI",     desc_short: "Menaxhim rezervimesh i optimizuar",      desc: "AI analizon pranitë dhe kërkesën për të sugjeruar orare optimale, mbushur listat e pritjes dhe balancuar trajnerët." },
        { title: "Përpunimi i pagesave",         desc_short: "Menaxhim automatik i të ardhurave",      desc: "Faturim i përsëritur, plane pagesash, faturim dhe parashikime të gjeneruara nga AI. Të gjitha metodat e pagesës." },
        { title: "Analitikë parashikuese",       desc_short: "Inteligjencë biznesi në kohë reale",     desc: "AI nxjerr trende, parashikon orët kulmore, identifikon anëtarët në rrezik dhe rekomandon veprime në kohë reale." },
        { title: "Ushqim & trajnim me AI",       desc_short: "Plane personale për çdo anëtar",         desc: "AI ndërton plane diete dhe stërvitjeje nga qëllimet, metrikat trupore dhe progresi — dhe i përshtat automatikisht." },
      ],
    },
    showcase: {
      eyebrow: "Demo",
      heading: "Shiko AI në veprim",
      subheading: "Çdo palestër merr përvojën e saj me markë të plotë. Ja si duken të njëjtat mjete AI në biznese të ndryshme fitness në platformën tonë.",
      tabs: { diet: "Planifikues Diete AI", workout: "Krijues Stërvitjeje AI", promo: "Krijues Promosh AI" },
      comingSoon: "SHPEJT",
      dietDesc: "AI gjeneron plane vakte të personalizuara me gjurmim makro, baza ushqimore dhe përshtatje automatike — me markën e palestrës tënde.",
      workoutDesc: "Programe trajnimi rreth nivelit të fitnesit, rikuperimit dhe trendeve. Planet evoluojnë ndërsa anëtarët përparojnë.",
      promoDesc: "Promocione me AI që targetojnë anëtarët e duhur në kohën e duhur për të maksimizuar të ardhurat dhe mbajtjen.",
    },
    mobile: {
      eyebrow: "Mobile",
      heading: "Aplikacione iOS & Android për çdo palestër",
      sub: "Anëtarët marrin përvojë mobile me markë të plotë. Ti mban kontrollin e çdo versioni.",
      bullet1: "Markë e personalizuar — logo, ngjyra, ikonë",
      bullet2: "Hyrje me QR, rezervime, plane dhe dyqan",
      bullet3: "Njoftime push dhe mesazhe brenda aplikacionit",
    },
    benefits: {
      eyebrow: "Pse ne",
      heading: "Pse THE NOT FIT?",
      subheading: "Platforma më e avancuar e menaxhimit të fitness me AI, e besuar nga liderët e industrisë.",
      items: [
        { title: "Menaxhim Diete me AI",              desc: "Gjeneroni plane ushqyerjeje të personalizuara në sekonda. AI adapton makrot dhe oraret e vakteve bazuar në qëllimet, alergjiitë dhe metrikat trupore." },
        { title: "Krijues Planesh Stërvitjeje me AI", desc: "Programe trajnimi rreth nivelit të fitnesit, të dhënave të rikuperimit dhe trendeve të performancës. Planet evoluojnë automatikisht." },
        { title: "Kontroll Aksesi i Verifikuar",      desc: "Verifikime identiteti me AI nëpërmjet QR kodit, kartës NFC, PIN ose skanimit biometrik. Zbulimi i anomalive sinjalizon hyrje të dyshimta në kohë reale." },
        { title: "Njohuri Progresi",                  desc: "AI analizon përbërjen trupore, rekordet e forcës dhe standardet kardio për të zbuluar trende dhe parashikuar ngecje — duke dhënë hapa të qarta." },
        { title: "Motor Mbajtjeje",                   desc: "Modelet e mësimit makinerik parashikojnë largimin para se të ndodhë. AI aktivizon automatikisht oferta riangazhimi, shpërblime dhe fushata rikthimi." },
        { title: "Panel Operacionesh",                desc: "AI parashikon zënien e katit, kërkesat e klasave, optimizon oraret e trajnerëve dhe nxjerr mundësi të ardhurash — live në çdo vendndodhje." },
      ],
    },
    pricing: {
      eyebrow: "Çmimet",
      heading: "Modulare dhe transparente",
      subheading: "Paguani vetëm për modulet që ju duhen. Shtoni ose hiqni veçori ndërsa biznesi rritet.",
      comingSoon: "SHPEJT", soon: "SHPEJT", perMonth: "/muaj", perMonthEach: "/muaj secila",
      modules: {
        business:   { name: "Menaxhimi i Biznesit",  features: ["Hyrje Anëtarësh (QR, Kartë, PIN)", "Panel Multi-Vendndodhje", "Menaxhimi i Punonjësve", "Raporte & Analitikë"] },
        classes:    { name: "Klasa",                  features: ["Planifikim (i përsëritur & njëherësh)", "Rezervime & lista pritjeje", "Kapacitet & caktim trajnerësh", "Njoftime automatike"] },
        workout:    { name: "Stërvitje & Trajnim",    features: ["Krijues i Planit të Stërvitjes", "Regjistër Stërvitjeje", "Bibliotekë Ushtrimesh", "Raportim Performancës"] },
        diet:       { name: "Planifikues Diete",      features: ["Plane vakte të personalizuara", "Bazë ushqimore (ushqime lokale)", "Gjurmim kalori & makro", "Integrim me planin e stërvitjes"] },
        store:      { name: "Dyqan",                  features: ["Katalog produktesh & kategori", "Menaxhim & gjurmim porosish", "Rrjedha e konfigurueshme", "Ushqime, suplemente, merch & më shumë"] },
        whitelabel: { name: "White Label",            features: ["Aplikacione personale iOS & Android", "Faqe interneti e personalizuar", "Domen i personalizuar", "Personalizim i plotë i markës"] },
        marketing:  { name: "Marketing & CRM",        features: ["CRM me profile anëtarësh", "Rikujtues automatikë rinovimi", "Email, SMS, Push & WhatsApp", "Automatizim onboarding"] },
      },
      extraLocations: { name: "Vendndodhje Shtesë", features: ["Akses i plotë modulesh për vendndodhje", "Menaxhim & raporte të veçanta", "Markë e personalizuar për çdo palestër", "Operacione të pavarura"] },
      calculator: {
        title: "Kalkulator Çmimesh", selectAll: "Zgjidh të gjitha", extraLocations: "Vendndodhje shtesë",
        modules: (n) => `${n} modul${n !== 1 ? "e" : ""}`,
        extraLocs: (n) => `+${n} vendndodhje shtesë`,
        total: "Totali",
      },
      cta: { sub: "Filloni me bazën dhe shtoni module ndërsa rriteni.", btn1: "Fillo Tani", btn2: "Fol me shitjet" },
      offer: {
        eyebrow: "Ofertë me kohë të kufizuar",
        heading: "Çmim vjetor i plotë për vendndodhje",
        subheading: "Sigurohuni me 50,000 ALL/vit për çdo vendndodhje palestre — të gjitha modulet, të gjithë anëtarët, të gjitha pajisjet. Një zbritje masive ndaj planeve mujore modulare, e disponueshme për një kohë shumë të kufizuar.",
        unit: "/vit për vendndodhje",
        compare: "Deri në 90% zbritje nga çmimi standard mujor · Të gjitha modulet të përfshira",
        cta: "Merr ofertën",
      },
    },
    caseStudy: {
      eyebrow: "Rast studimi", heading1: "Duke fuqizuar", heading2: "Flex Gym Group",
      description: "Mbi 3,000 anëtarë nëpër vendndodhje të shumëfishta — të menaxhuar pa probleme në THE NOT FIT. Nga palestrat premium tek studiot CrossFit dhe restorantet, një sistem bën gjithçka.",
      gyms: [
        { subtitle: "Palestër premium në kryeqytet",  members: "2,000+", highlight: "Anëtarë", features: ["Kat i plotë pajisjesh", "Klasa grupore", "Trajnim personal", "Sauna & rikuperim"] },
        { subtitle: "Palestër premium në bregdet",    members: "1,500+", highlight: "Anëtarë", features: ["Trajnim në bregdet", "Zona jashtë", "Programe sezonale", "Integrim resort"] },
        { subtitle: "CrossFit, pilates & restorant",  members: "1,200+", highlight: "Anëtarë", features: ["Box CrossFit", "Studio Pilates", "Restorant i brendshëm", "Bar ushqyerjeje sportive"] },
      ],
      followInstagram: "Ndiqni në Instagram",
      testimonial: "THE NOT FIT na lejon të menaxhojmë 3 koncepte krejt të ndryshme palestrash — nga qendra premium fitness deri te box CrossFit me restorant — të gjitha nën një platformë. Anëtarët tanë e duan përvojën e qetë.",
      operator: "Operues fitness me shumë vendndodhje, Shqipëri",
      statsLabels: ["Vendndodhje", "Anëtarë gjithsej", "Klasa / javë", "Platformë"],
    },
    cta: {
      heading1: "Gati të shkallëzoni", heading2: "biznesin tuaj me AI?",
      description: "Bashkohuni me bizneset fitness që përdorin mjetet me AI të THE NOT FIT për të automatizuar dhe rritur.\nFilloni provën falas 14-ditore sot — pa kartë krediti.",
      btn1: "Fillo provën falas", btn2: "Fol me shitjet",
      check1: "Provë falas 14-ditore", check2: "Pa kartë krediti", check3: "Anulo në çdo kohë",
    },
    footer: {
      tagline: "Platformë e menaxhimit të fitness me AI për palestra, studio dhe biznese fitness.",
      productHeading: "Produkti",
      productLinks: [
        { label: "Veçoritë",  href: "#features" },
        { label: "Demo",      href: "#showcase" },
        { label: "Çmimet",    href: "#pricing" },
        { label: "Klientët",  href: "#customers" },
      ],
      legalHeading: "Ligjore",
      legalLinks: [
        { label: "Politika e Privatësisë", href: "/privacy" },
        { label: "Kushtet e Shërbimit",    href: "/privacy" },
        { label: "Fshirja e të Dhënave",   href: "/data-deletion" },
        { label: "Mbështetje",             href: "/support" },
      ],
      copyright: "© 2026 THE NOT FIT. Të gjitha të drejtat e rezervuara.",
    },
  },

  // ── Italian / Italiano ─────────────────────────────────────────────────────
  it: {
    announcement: { eyebrow: "Tempo limitato", message: "€499/anno per sede · Fino al 90% di sconto sul mensile", cta: "Vedi offerta" },
    nav: { signIn: "Accedi", getStarted: "Inizia", features: "Funzionalità", showcase: "Demo", pricing: "Prezzi", customers: "Clienti" },
    hero: {
      badge: "Piattaforma fitness con AI",
      heading1: "Gestisci la tua attività fitness con",
      heading2: "automazione intelligente.",
      description: "La piattaforma che alimenta le moderne catene di palestre. Automatizza le operazioni, personalizza l'esperienza dei soci e cresci con l'AI pensata per il fitness.",
      cta1: "Inizia la prova gratuita",
      cta2: "Prenota una demo",
      footnote: "Nessuna carta · Prova 14 giorni · Cancella quando vuoi",
      logoBar: "Scelta degli operatori fitness leader",
      offerPill: "Offerta lancio a tempo limitato · €499/anno per sede",
    },
    stats: { gyms: "Palestre", activeMembers: "Soci attivi", uptimeSla: "Uptime SLA", support: "Supporto" },
    features: {
      eyebrow: "Piattaforma",
      heading: "Tutto per gestire una palestra moderna",
      subheading: "Strumenti modulari che automatizzano, ottimizzano e personalizzano ogni parte del tuo business fitness.",
      valueProp: "L'AI fa il lavoro. Tu gestisci la palestra.",
      cards: [
        { title: "Multi-tenancy white-label", desc_short: "Per franchising e catene",            desc: "Gestisci sedi illimitate da un'unica piattaforma. Trend di performance per sede; ogni palestra mantiene il suo brand." },
        { title: "Promo builder con AI",      desc_short: "Aumenta i ricavi con promo smart",     desc: "L'AI progetta promozioni mirate, trova il momento migliore e personalizza gli sconti per massimizzare le conversioni." },
        { title: "Pianificazione corsi AI",   desc_short: "Gestione prenotazioni ottimizzata",    desc: "L'AI analizza presenze e domanda per suggerire orari ottimali, riempire le liste d'attesa e bilanciare gli istruttori." },
        { title: "Elaborazione pagamenti",    desc_short: "Gestione automatica dei ricavi",       desc: "Fatturazione ricorrente, piani di pagamento, fatturazione e previsioni AI. Tutti i principali metodi di pagamento." },
        { title: "Analisi predittive",        desc_short: "Business intelligence in tempo reale", desc: "L'AI evidenzia trend di ricavi, prevede ore di punta, identifica soci a rischio e raccomanda azioni in tempo reale." },
        { title: "Nutrizione & training AI",  desc_short: "Piani personali per ogni socio",       desc: "L'AI costruisce piani dieta e allenamento da obiettivi, metriche corporee e progressi — e li adatta automaticamente." },
      ],
    },
    showcase: {
      eyebrow: "Demo",
      heading: "L'AI in azione",
      subheading: "Ogni palestra ha la propria esperienza completamente personalizzata. Ecco come appaiono gli stessi strumenti AI nelle diverse attività fitness sulla piattaforma.",
      tabs: { diet: "Pianificatore Dieta AI", workout: "Costruttore Allenamenti AI", promo: "Creatore Promo AI" },
      comingSoon: "PROSSIMAMENTE",
      dietDesc: "L'AI genera piani pasto personalizzati con monitoraggio macro, database alimentari e regolazioni automatiche — con il brand di ogni palestra.",
      workoutDesc: "Programmi intelligenti basati su livello di fitness, recupero e tendenze. I piani si evolvono con i progressi dei soci.",
      promoDesc: "Promozioni AI che raggiungono i soci giusti al momento giusto per massimizzare ricavi e fidelizzazione.",
    },
    mobile: {
      eyebrow: "Mobile",
      heading: "App iOS & Android native per ogni palestra",
      sub: "I soci ricevono un'esperienza mobile completamente personalizzata. Tu controlli ogni versione.",
      bullet1: "Brand personalizzato — logo, colori, icona",
      bullet2: "Check-in QR, prenotazioni, piani e store",
      bullet3: "Notifiche push e messaggi in-app",
    },
    benefits: {
      eyebrow: "Perché noi",
      heading: "Perché THE NOT FIT?",
      subheading: "La piattaforma di gestione fitness con AI più avanzata, scelta dai leader del settore.",
      items: [
        { title: "Gestione Dieta con AI",                   desc: "Genera piani nutrizionali personalizzati in pochi secondi. L'AI adatta i macro e gli orari dei pasti in base agli obiettivi, alle allergie e alle metriche corporee." },
        { title: "Costruttore Piani di Allenamento con AI", desc: "Programmi di allenamento basati su livello di fitness, dati di recupero e tendenze di prestazione. I piani si evolvono automaticamente." },
        { title: "Controllo Accessi Verificato",            desc: "Verifiche d'identità con AI tramite QR code, tessera NFC, PIN o scansione biometrica. Il rilevamento anomalie segnala accessi sospetti in tempo reale." },
        { title: "Analisi Progressi",                       desc: "L'AI analizza composizione corporea, record di forza e benchmark cardio per evidenziare trend e prevedere plateau — passi concreti per istruttori e soci." },
        { title: "Motore di Fidelizzazione",                desc: "Modelli ML prevedono il churn prima che avvenga. L'AI attiva automaticamente offerte personalizzate, premi streak e campagne di riconquista." },
        { title: "Dashboard Operativo",                     desc: "L'AI prevede l'occupazione, la domanda dei corsi, ottimizza gli orari e segnala opportunità di ricavo — live in ogni sede." },
      ],
    },
    pricing: {
      eyebrow: "Prezzi",
      heading: "Modulare e trasparente",
      subheading: "Paga solo per i moduli di cui hai bisogno. Aggiungi o rimuovi funzionalità man mano che il business cresce.",
      comingSoon: "PROSSIMAMENTE", soon: "PRESTO", perMonth: "/mese", perMonthEach: "/mese cad.",
      modules: {
        business:   { name: "Gestione Aziendale",         features: ["Check-in Soci (QR, Tessera, PIN)", "Dashboard Multi-Sede", "Gestione Dipendenti", "Report & Analisi"] },
        classes:    { name: "Corsi",                       features: ["Pianificazione (ricorrente & una tantum)", "Prenotazioni & liste d'attesa", "Capienza & assegnazione istruttori", "Notifiche automatiche"] },
        workout:    { name: "Allenamento & Training",      features: ["Creatore Piano di Allenamento", "Diario Allenamento", "Libreria Esercizi", "Report Prestazioni"] },
        diet:       { name: "Pianificatore Dieta",         features: ["Piani pasto personalizzati", "Database alimenti (cibi locali)", "Monitoraggio calorie & macro", "Integrazione piano allenamento"] },
        store:      { name: "Negozio",                     features: ["Catalogo prodotti & categorie", "Gestione & monitoraggio ordini", "Flussi ordini configurabili", "Cibo, integratori, merch e altro"] },
        whitelabel: { name: "White Label",                 features: ["App iOS & Android personalizzate", "Sito web personalizzato", "Dominio personalizzato", "Personalizzazione completa del brand"] },
        marketing:  { name: "Marketing & CRM",             features: ["CRM con profili soci", "Promemoria rinnovo automatici", "Email, SMS, Push & WhatsApp", "Automazione onboarding"] },
      },
      extraLocations: { name: "Sedi Aggiuntive", features: ["Accesso completo ai moduli per sede", "Gestione & report separati", "Branding personalizzato per palestra", "Operazioni indipendenti"] },
      calculator: {
        title: "Calcolatore prezzi", selectAll: "Seleziona tutti", extraLocations: "Sedi aggiuntive",
        modules: (n) => `${n} modul${n !== 1 ? "i" : "o"}`,
        extraLocs: (n) => `+${n} sed${n !== 1 ? "i" : "e"} aggiuntiv${n !== 1 ? "e" : "a"}`,
        total: "Totale",
      },
      cta: { sub: "Inizia con l'essenziale e aggiungi moduli man mano che cresci.", btn1: "Inizia ora", btn2: "Parla con le vendite" },
      offer: {
        eyebrow: "Offerta lancio a tempo limitato",
        heading: "Prezzo annuale completo per sede",
        subheading: "Blocca €499/anno per ogni sede di palestra — ogni modulo, ogni socio, ogni dispositivo. Uno sconto enorme rispetto ai piani modulari mensili, disponibile per un tempo molto limitato mentre apriamo i piani multi-sede.",
        unit: "/anno per sede",
        compare: "Fino al 90% di sconto sul prezzo standard mensile · Tutti i moduli inclusi",
        cta: "Riscatta offerta",
      },
    },
    caseStudy: {
      eyebrow: "Caso di studio", heading1: "Al servizio del", heading2: "Flex Gym Group",
      description: "Oltre 3.000 soci in più sedi — gestiti senza problemi su THE NOT FIT. Dalle palestre premium ai box CrossFit ai ristoranti interni, un solo sistema.",
      gyms: [
        { subtitle: "Palestra premium nella capitale", members: "2.000+", highlight: "Soci", features: ["Piano attrezzature completo", "Corsi di gruppo", "Personal training", "Sauna & recupero"] },
        { subtitle: "Palestra premium sul mare",        members: "1.500+", highlight: "Soci", features: ["Allenamento in riva al mare", "Aree esterne", "Programmi stagionali", "Integrazione resort"] },
        { subtitle: "CrossFit, pilates & ristorante",  members: "1.200+", highlight: "Soci", features: ["Box CrossFit", "Studio Pilates", "Ristorante interno", "Bar nutrizione sportiva"] },
      ],
      followInstagram: "Seguici su Instagram",
      testimonial: "THE NOT FIT ci permette di gestire 3 concetti di palestra completamente diversi — da un centro fitness premium a una box CrossFit con ristorante — tutto sotto un'unica piattaforma. I nostri soci adorano l'esperienza.",
      operator: "Operatore fitness multi-sede, Albania",
      statsLabels: ["Sedi", "Soci totali", "Corsi / settimana", "Piattaforma"],
    },
    cta: {
      heading1: "Pronto a scalare il tuo", heading2: "business fitness con l'AI?",
      description: "Unisciti alle imprese fitness che usano gli strumenti AI di THE NOT FIT per automatizzare e crescere.\nInizia la prova gratuita di 14 giorni oggi — nessuna carta di credito richiesta.",
      btn1: "Inizia la prova gratuita", btn2: "Parla con le vendite",
      check1: "Prova gratuita 14 giorni", check2: "Nessuna carta di credito", check3: "Cancella quando vuoi",
    },
    footer: {
      tagline: "Piattaforma di gestione fitness con AI per palestre, studi e imprese fitness.",
      productHeading: "Prodotto",
      productLinks: [
        { label: "Funzionalità", href: "#features" },
        { label: "Demo",         href: "#showcase" },
        { label: "Prezzi",       href: "#pricing" },
        { label: "Clienti",      href: "#customers" },
      ],
      legalHeading: "Legale",
      legalLinks: [
        { label: "Informativa sulla Privacy", href: "/privacy" },
        { label: "Termini di Servizio",       href: "/privacy" },
        { label: "Cancellazione Dati",        href: "/data-deletion" },
        { label: "Supporto",                  href: "/support" },
      ],
      copyright: "© 2026 THE NOT FIT. Tutti i diritti riservati.",
    },
  },
}

// ─── Static data ─────────────────────────────────────────────────────────────

const MODULES_CONFIG: { id: ModuleKey; price: number; comingSoon?: boolean; icon: typeof Building2 }[] = [
  { id: "business",   price: 149, icon: Building2 },
  { id: "classes",    price: 149, icon: Calendar },
  { id: "workout",    price: 99,  icon: Dumbbell },
  { id: "diet",       price: 99,  icon: Salad },
  { id: "store",      price: 99,  icon: ShoppingBag },
  { id: "whitelabel", price: 99,  icon: Globe },
  { id: "marketing",  price: 49,  icon: Megaphone, comingSoon: true },
]

const LOCATION_PRICE = 49

const FEATURE_ICONS = [Globe, Megaphone, Calendar, CreditCard, BarChart3, Bot]

const GYMS = [
  { name: "FlexGym Tirana",   instagram: "https://www.instagram.com/flexgym_tirane/",       image: "https://gyms.fra1.digitaloceanspaces.com/website/flex/flexgym.jpg",    logo: "https://gyms.fra1.digitaloceanspaces.com/logos/flexgym-ml9wd9l5/87ce35b0-0ce1-45aa-92c8-52bb71fb8a18.png" },
  { name: "Flex Green Coast", instagram: "https://www.instagram.com/flexgymgreencoast.al/", image: "https://gyms.fra1.digitaloceanspaces.com/website/flex/greencoast.jpg", logo: "https://gyms.fra1.digitaloceanspaces.com/logos/flexgym-ml9wd9l5/87ce35b0-0ce1-45aa-92c8-52bb71fb8a18.png" },
  { name: "Flex Sports Club", instagram: "https://www.instagram.com/flexsportsclub.al/",    image: "https://gyms.fra1.digitaloceanspaces.com/website/flex/classes.jpg",    logo: "https://gyms.fra1.digitaloceanspaces.com/logos/flexgym-ml9wd9l5/87ce35b0-0ce1-45aa-92c8-52bb71fb8a18.png" },
]

const TENANT_SHOWCASE_THEMES = [
  { accent: "#E63946", dark: true,  bg: "#111111", text: "#f5f5f5", muted: "#a1a1aa" },
  { accent: "#FFD60C", dark: true,  bg: "#111111", text: "#f5f5f5", muted: "#a1a1aa" },
  { accent: "#1B1917", dark: false, bg: "#fafaf9", text: "#1B1917", muted: "#78716c" },
]

// ─── Background mesh ─────────────────────────────────────────────────────────

function HeroMesh() {
  return (
    <div aria-hidden className="absolute inset-0 -z-10 overflow-hidden">
      <div className="absolute -top-32 -left-32 h-[480px] w-[480px] rounded-full bg-primary/20 blur-[120px] dark:bg-primary/15" />
      <div className="absolute -bottom-40 right-0 h-[520px] w-[520px] rounded-full bg-primary/10 blur-[140px] dark:bg-primary/10" />
      <div
        className="absolute inset-0 opacity-[0.04] dark:opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }}
      />
    </div>
  )
}

function CtaMesh() {
  return (
    <div aria-hidden className="absolute inset-0 overflow-hidden">
      <div className="absolute -top-40 left-1/2 h-[520px] w-[720px] -translate-x-1/2 rounded-full bg-primary/30 blur-[140px]" />
      <div className="absolute -bottom-32 -right-20 h-[400px] w-[400px] rounded-full bg-primary/20 blur-[120px]" />
    </div>
  )
}

// ─── Brand mark (red square + green circle + blue triangle) ──────────────────

const BRAND_RED = "#C0392B"
const BRAND_GREEN = "#4A8C3F"
const BRAND_BLUE = "#2E5BA8"
const BRAND_COLORS = [BRAND_RED, BRAND_GREEN, BRAND_BLUE]

type ShapeProps = { className?: string; size?: number; opacity?: number; color?: string }

function ShapeSquare({ className = "", size = 40, opacity = 0.1, color = BRAND_RED }: ShapeProps) {
  return <div aria-hidden className={`absolute pointer-events-none ${className}`} style={{ width: size, height: size, backgroundColor: color, opacity }} />
}
function ShapeCircle({ className = "", size = 40, opacity = 0.1, color = BRAND_GREEN }: ShapeProps) {
  return <div aria-hidden className={`absolute pointer-events-none rounded-full ${className}`} style={{ width: size, height: size, backgroundColor: color, opacity }} />
}
function ShapeTriangle({ className = "", size = 40, opacity = 0.1, color = BRAND_BLUE }: ShapeProps) {
  return (
    <svg aria-hidden className={`absolute pointer-events-none ${className}`} width={size} height={size} viewBox="0 0 40 40" style={{ opacity }}>
      <polygon points="2,2 38,2 20,38" fill={color} />
    </svg>
  )
}
function ShapeRing({ className = "", size = 40, opacity = 0.12, color = BRAND_BLUE }: ShapeProps) {
  return (
    <div
      aria-hidden
      className={`absolute pointer-events-none rounded-full ${className}`}
      style={{ width: size, height: size, border: `${Math.max(2, size * 0.08)}px solid ${color}`, opacity }}
    />
  )
}

/**
 * Six layout presets for shape decoration. Each preset places a small triplet
 * (square / circle / triangle) in different positions to give each section
 * a unique, hand-arranged feel without re-using the same composition twice.
 */
function SectionDecor({ variant = "a" }: { variant?: "a" | "b" | "c" | "d" | "e" | "f" }) {
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
      {variant === "a" && (
        <>
          <ShapeSquare   className="top-12 right-[8%] rotate-12"  size={56} opacity={0.09} />
          <ShapeCircle   className="bottom-16 left-[6%]"          size={72} opacity={0.08} />
          <ShapeTriangle className="top-[55%] right-[24%]"        size={40} opacity={0.10} />
          <ShapeRing     className="top-20 left-[28%]"            size={36} opacity={0.10} color={BRAND_RED} />
        </>
      )}
      {variant === "b" && (
        <>
          <ShapeCircle   className="top-10 right-[12%]"           size={56} opacity={0.09} />
          <ShapeSquare   className="bottom-12 right-[5%] rotate-45" size={40} opacity={0.08} />
          <ShapeTriangle className="top-1/2 left-[5%] -rotate-12"  size={48} opacity={0.10} />
        </>
      )}
      {variant === "c" && (
        <>
          <ShapeTriangle className="top-16 left-[10%] rotate-12"   size={52} opacity={0.09} />
          <ShapeCircle   className="bottom-20 right-[10%]"          size={60} opacity={0.08} />
          <ShapeSquare   className="top-1/2 right-[35%]"            size={32} opacity={0.10} />
          <ShapeRing     className="bottom-12 left-[30%]"           size={40} opacity={0.10} color={BRAND_GREEN} />
        </>
      )}
      {variant === "d" && (
        <>
          <ShapeSquare   className="top-8 left-[15%] -rotate-12"   size={36} opacity={0.09} />
          <ShapeCircle   className="bottom-10 left-[8%]"            size={48} opacity={0.08} />
          <ShapeTriangle className="top-12 right-[10%]"             size={44} opacity={0.10} />
        </>
      )}
      {variant === "e" && (
        <>
          <ShapeCircle   className="top-20 left-[8%]"               size={64} opacity={0.08} />
          <ShapeTriangle className="bottom-16 right-[14%] rotate-12" size={48} opacity={0.10} />
          <ShapeSquare   className="top-[40%] right-[6%]"            size={36} opacity={0.09} />
          <ShapeRing     className="top-10 right-[35%]"              size={28} opacity={0.10} color={BRAND_RED} />
        </>
      )}
      {variant === "f" && (
        <>
          <ShapeSquare   className="top-12 right-[5%]"              size={44} opacity={0.09} />
          <ShapeTriangle className="bottom-12 left-[10%] -rotate-6" size={40} opacity={0.10} />
          <ShapeCircle   className="top-1/2 right-[30%]"             size={28} opacity={0.10} />
        </>
      )}
    </div>
  )
}

function BrandMark({ size = 10, className = "" }: { size?: number; className?: string }) {
  return (
    <span aria-hidden className={`inline-flex items-center gap-[2px] leading-none flex-shrink-0 ${className}`}>
      <span className="block" style={{ width: size, height: size, backgroundColor: BRAND_RED }} />
      <span className="block rounded-full" style={{ width: size, height: size, backgroundColor: BRAND_GREEN }} />
      <svg width={size} height={size} viewBox="0 0 12 12" className="block">
        <polygon points="0,0 12,0 6,12" fill={BRAND_BLUE} />
      </svg>
    </span>
  )
}

// ─── Announcement bar (limited-time offer) ───────────────────────────────────

function AnnouncementBar({ eyebrow, message, cta }: { eyebrow: string; message: string; cta: string }) {
  return (
    <div className="relative bg-zinc-950 text-white overflow-hidden">
      {/* Tri-color gradient wash */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-50"
        style={{
          background: `linear-gradient(90deg, ${BRAND_RED}40 0%, ${BRAND_GREEN}33 50%, ${BRAND_BLUE}40 100%)`,
        }}
      />
      {/* Top + bottom tri-color hairlines */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, ${BRAND_RED}, ${BRAND_GREEN}, ${BRAND_BLUE})` }}
      />
      <div
        aria-hidden
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: `linear-gradient(90deg, ${BRAND_BLUE}, ${BRAND_GREEN}, ${BRAND_RED})` }}
      />
      <div className="container mx-auto px-4 relative">
        <a
          href="#offer"
          className="flex items-center justify-center gap-2 sm:gap-3 py-2 text-xs sm:text-sm group"
        >
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-white/10 backdrop-blur border border-white/20 font-bold tracking-wider text-[10px] uppercase">
            <Clock className="w-3 h-3" strokeWidth={2.5} />
            {eyebrow}
          </span>
          <BrandMark size={8} className="hidden sm:inline-flex" />
          <span className="font-medium truncate">{message}</span>
          <span className="hidden sm:inline-flex items-center gap-1 font-semibold text-white/90 group-hover:text-white transition-colors">
            {cta}
            <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
          </span>
        </a>
      </div>
    </div>
  )
}

// ─── Language Picker ─────────────────────────────────────────────────────────

function LanguagePicker({ lang, setLang, upward = false }: { lang: Lang; setLang: (l: Lang) => void; upward?: boolean }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)
  const current = LANGS.find(l => l.code === lang)!

  useEffect(() => {
    function onOutsideClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onOutsideClick)
    return () => document.removeEventListener("mousedown", onOutsideClick)
  }, [])

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-border hover:bg-secondary transition-colors select-none text-sm"
        aria-label="Select language"
      >
        <span className="font-medium leading-none">{current.label}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className={`absolute right-0 w-44 rounded-xl border bg-background shadow-xl overflow-hidden z-50 py-1 ${upward ? "bottom-full mb-2" : "top-full mt-2"}`}>
          {LANGS.map(l => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false) }}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors hover:bg-secondary ${lang === l.code ? "font-semibold" : "font-normal"}`}
            >
              <span className="flex-1 text-left leading-none">{l.label}</span>
              {lang === l.code && <Check className="w-3.5 h-3.5 text-primary flex-shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Showcase mocks ──────────────────────────────────────────────────────────

function BrowserChrome({ children, accent }: { children: React.ReactNode; accent?: string }) {
  return (
    <div className="rounded-2xl border bg-background shadow-2xl overflow-hidden">
      <div className="flex items-center gap-1.5 px-4 py-3 border-b bg-secondary/40">
        <div className="w-2.5 h-2.5 rounded-full bg-muted-foreground/30" />
        <div className="w-2.5 h-2.5 rounded-full bg-muted-foreground/30" />
        <div className="w-2.5 h-2.5 rounded-full bg-muted-foreground/30" />
        {accent && (
          <div className="ml-3 px-2.5 py-1 rounded-md bg-background border text-[11px] font-mono text-muted-foreground">
            app.thenotfit.com
          </div>
        )}
      </div>
      {children}
    </div>
  )
}

function DietPlannerMock({ themeIdx = 0 }: { themeIdx?: number }) {
  const th = TENANT_SHOWCASE_THEMES[themeIdx]
  return (
    <div className="p-5 md:p-6" style={{ color: th.text, backgroundColor: th.bg }}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.18em] mb-1" style={{ color: th.muted }}>AI-GENERATED PLAN</div>
          <div className="text-lg font-bold">Weight Loss Plan</div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ backgroundColor: `${th.accent}20` }}>
          <Sparkles className="w-3.5 h-3.5" style={{ color: th.accent }} />
          <span className="text-[11px] font-semibold" style={{ color: th.accent }}>AI</span>
        </div>
      </div>
      <div className="flex gap-1 mb-5 overflow-x-auto pb-1">
        {["Day 1", "Day 2", "Day 3", "Day 4", "Day 5", "Day 6", "Day 7"].map((day, i) => (
          <div
            key={day}
            className="px-2.5 py-1 rounded-md text-[11px] font-medium whitespace-nowrap"
            style={i === 0
              ? { backgroundColor: th.accent, color: "#fff" }
              : { backgroundColor: th.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)", color: th.muted }
            }
          >
            {day}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        {[
          { meal: "Breakfast", time: "7:30",  foods: [{ n: "Greek yogurt", c: 180 }, { n: "Whole-grain toast", c: 120 }, { n: "Green tea", c: 5 }],  kcal: 305 },
          { meal: "Lunch",     time: "12:30", foods: [{ n: "Chicken salad", c: 350 }, { n: "Quinoa", c: 185 }, { n: "Olive oil", c: 90 }],            kcal: 625 },
          { meal: "Dinner",    time: "19:00", foods: [{ n: "Baked salmon", c: 380 }, { n: "Steamed broccoli", c: 55 }, { n: "Brown rice", c: 215 }],   kcal: 650 },
        ].map(card => (
          <div key={card.meal} className="rounded-lg p-3" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-semibold">{card.meal}</span>
              <span className="text-[10px]" style={{ color: th.muted }}>{card.time}</span>
            </div>
            <div className="space-y-1 mb-2">
              {card.foods.map(food => (
                <div key={food.n} className="flex items-center justify-between text-[11px]">
                  <span className="truncate mr-2">{food.n}</span>
                  <span className="font-medium">{food.c}</span>
                </div>
              ))}
            </div>
            <div className="border-t pt-1.5 flex justify-between items-center text-[10px]" style={{ borderColor: th.dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
              <span style={{ color: th.muted }}>Total</span>
              <span className="font-bold" style={{ color: th.accent }}>{card.kcal} kcal</span>
            </div>
          </div>
        ))}
      </div>
      <div className="rounded-lg p-3" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold">Daily macros</span>
          <span className="text-[11px] font-semibold" style={{ color: th.accent }}>1,580 / 1,650 kcal</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
          <div className="h-full" style={{ width: "92%", backgroundColor: th.accent }} />
        </div>
      </div>
    </div>
  )
}

function WorkoutMock({ themeIdx = 1 }: { themeIdx?: number }) {
  const th = TENANT_SHOWCASE_THEMES[themeIdx]
  return (
    <div className="p-5 md:p-6" style={{ color: th.text, backgroundColor: th.bg }}>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.18em] mb-1" style={{ color: th.muted }}>AI-GENERATED PLAN</div>
          <div className="text-lg font-bold">Full Body Strength</div>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${th.accent}25`, color: th.accent }}>Intermediate</span>
            <span className="text-[10px]" style={{ color: th.muted }}>45 min</span>
          </div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ backgroundColor: `${th.accent}20` }}>
          <Sparkles className="w-3.5 h-3.5" style={{ color: th.accent }} />
          <span className="text-[11px] font-semibold" style={{ color: th.accent }}>AI</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-5">
        {["Chest", "Back", "Legs", "Core", "Shoulders"].map(muscle => (
          <span key={muscle} className="text-[10px] px-2.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: `${th.accent}15`, color: th.accent }}>
            {muscle}
          </span>
        ))}
      </div>
      <div className="rounded-lg overflow-hidden mb-4" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
        <div className="grid grid-cols-12 gap-2 px-3 py-2 text-[10px] font-semibold tracking-wider uppercase" style={{ color: th.muted, borderBottom: `1px solid ${th.dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}` }}>
          <div className="col-span-5">Exercise</div>
          <div className="col-span-2 text-center">Sets</div>
          <div className="col-span-2 text-center">Reps</div>
          <div className="col-span-3 text-center">Rest</div>
        </div>
        {[
          { name: "Barbell Bench Press", sets: 4, reps: "8-10", rest: "90s" },
          { name: "Bent-Over Row",       sets: 4, reps: "8-10", rest: "90s" },
          { name: "Barbell Squat",       sets: 4, reps: "6-8",  rest: "120s" },
          { name: "Overhead Press",      sets: 3, reps: "10-12", rest: "60s" },
          { name: "Plank Hold",          sets: 3, reps: "45s",   rest: "45s" },
        ].map((ex, i, arr) => (
          <div key={ex.name} className="grid grid-cols-12 gap-2 px-3 py-2 text-xs items-center" style={{ borderBottom: i < arr.length - 1 ? `1px solid ${th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.04)"}` : undefined }}>
            <div className="col-span-5 font-medium">{ex.name}</div>
            <div className="col-span-2 text-center font-semibold" style={{ color: th.accent }}>{ex.sets}</div>
            <div className="col-span-2 text-center">{ex.reps}</div>
            <div className="col-span-3 text-center" style={{ color: th.muted }}>{ex.rest}</div>
          </div>
        ))}
      </div>
      <div className="rounded-lg p-3" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)" }}>
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-semibold">Workout progress</span>
          <span className="text-[11px] font-semibold" style={{ color: th.accent }}>3 of 5 completed</span>
        </div>
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: th.dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)" }}>
          <div className="h-full" style={{ width: "60%", backgroundColor: th.accent }} />
        </div>
      </div>
    </div>
  )
}

function PromoMock({ comingSoonLabel }: { comingSoonLabel: string }) {
  const th = TENANT_SHOWCASE_THEMES[2]
  return (
    <div className="relative p-5 md:p-6" style={{ color: th.text, backgroundColor: th.bg }}>
      <div className="filter blur-[2px] opacity-60 pointer-events-none select-none">
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.18em] mb-1" style={{ color: th.muted }}>AI-GENERATED PROMOTION</div>
            <div className="text-lg font-bold">Summer Membership Drive</div>
          </div>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md" style={{ backgroundColor: `${th.accent}15` }}>
            <Sparkles className="w-3.5 h-3.5" style={{ color: th.accent }} />
            <span className="text-[11px] font-semibold" style={{ color: th.accent }}>AI</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 mb-5">
          {[
            { title: "New Member Welcome",  discount: "30%", audience: "New Sign-ups",     period: "First Month" },
            { title: "Annual Plan Upgrade", discount: "20%", audience: "Monthly Members",  period: "Limited Time" },
            { title: "Referral Bonus",      discount: "€25", audience: "Active Members",   period: "Ongoing" },
          ].map(promo => (
            <div key={promo.title} className="rounded-lg p-3" style={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
              <div className="text-xl font-bold mb-1" style={{ color: th.accent }}>{promo.discount}</div>
              <div className="text-xs font-semibold mb-2">{promo.title}</div>
              <div className="space-y-0.5 text-[10px]" style={{ color: th.muted }}>
                <div className="flex justify-between"><span>Target</span><span>{promo.audience}</span></div>
                <div className="flex justify-between"><span>Period</span><span>{promo.period}</span></div>
              </div>
            </div>
          ))}
        </div>
        <div className="rounded-lg p-3" style={{ backgroundColor: "rgba(0,0,0,0.03)" }}>
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: "Predicted Conversion", value: "24%" },
              { label: "Estimated Revenue",    value: "€3,200" },
              { label: "Members Targeted",     value: "450" },
            ].map(stat => (
              <div key={stat.label} className="text-center">
                <div className="text-base font-bold" style={{ color: th.accent }}>{stat.value}</div>
                <div className="text-[10px]" style={{ color: th.muted }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-foreground text-background font-semibold text-xs tracking-wider shadow-xl">
          <Sparkles className="w-3.5 h-3.5" />
          {comingSoonLabel}
        </div>
      </div>
    </div>
  )
}

// ─── Phone Mockup (mobile callout) ───────────────────────────────────────────

function AdminDashboardMock() {
  const kpis = [
    { label: "Check-ins today",  value: "247",    delta: "+18 vs yesterday", Icon: ScanLine,   color: BRAND_RED },
    { label: "Active members",   value: "2,340",  delta: "↑ 4.2% this week",  Icon: Users,      color: BRAND_GREEN },
    { label: "Revenue today",    value: "€1,247", delta: "↑ 8.1% MoM",        Icon: CreditCard, color: BRAND_BLUE },
  ]
  const hourly = [14, 22, 18, 26, 30, 12, 16, 20, 28, 34, 24, 18]
  const peak = Math.max(...hourly)
  const activity = [
    { who: "Andi Bardhi",  what: "checked in via QR",         time: "09:42", color: BRAND_RED },
    { who: "Klara Hoxha",  what: "membership renewed · €49",  time: "09:38", color: BRAND_GREEN },
    { who: "Era Krasniqi", what: "booked HIIT 18:30",         time: "09:31", color: BRAND_BLUE },
    { who: "Bledi Shehu",  what: "personal training booked",  time: "09:24", color: BRAND_RED },
  ]
  return (
    <div className="bg-white p-5 md:p-6 text-zinc-900">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500 mb-1">DASHBOARD · TIRANA</div>
          <div className="text-lg font-bold tracking-tight">Today's overview</div>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-zinc-100">
          <span className="relative flex w-1.5 h-1.5">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75 animate-ping" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500" />
          </span>
          <span className="text-[10px] font-semibold tracking-wider uppercase text-zinc-700">Live</span>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-3 gap-2.5 mb-4">
        {kpis.map(kpi => (
          <div key={kpi.label} className="rounded-lg border border-zinc-200 p-3">
            <div className="flex items-center gap-1.5 mb-2">
              <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: `${kpi.color}1A` }}>
                <kpi.Icon className="w-3 h-3" style={{ color: kpi.color }} strokeWidth={2.25} />
              </div>
              <span className="text-[10px] font-medium text-zinc-500 truncate">{kpi.label}</span>
            </div>
            <div className="text-xl font-bold tracking-tight tabular-nums">{kpi.value}</div>
            <div className="text-[10px] font-semibold text-emerald-600 mt-0.5">{kpi.delta}</div>
          </div>
        ))}
      </div>

      {/* Hourly check-ins chart */}
      <div className="rounded-lg border border-zinc-200 p-3 mb-3">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500 mb-0.5">CHECK-INS BY HOUR</div>
            <div className="text-xs font-medium text-zinc-700">Peak {peak} at 18:00</div>
          </div>
          <div className="text-[10px] font-medium text-zinc-500">Last 12h</div>
        </div>
        <div className="flex items-end gap-1 h-14">
          {hourly.map((v, i) => {
            const color = i % 3 === 0 ? BRAND_RED : i % 3 === 1 ? BRAND_GREEN : BRAND_BLUE
            return (
              <div
                key={i}
                className="flex-1 rounded-t-sm"
                style={{ height: `${(v / peak) * 100}%`, backgroundColor: color, opacity: 0.85 }}
              />
            )
          })}
        </div>
      </div>

      {/* Activity feed */}
      <div className="rounded-lg border border-zinc-200 p-3">
        <div className="flex items-center justify-between mb-2.5">
          <div className="text-[10px] font-semibold tracking-[0.18em] uppercase text-zinc-500">RECENT ACTIVITY</div>
          <div className="text-[10px] font-medium text-zinc-400">All locations</div>
        </div>
        <div className="space-y-2">
          {activity.map(item => (
            <div key={item.who} className="flex items-center gap-2.5 text-[11px]">
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: item.color }} />
              <span className="font-semibold text-zinc-900 truncate">{item.who}</span>
              <span className="text-zinc-500 truncate">{item.what}</span>
              <span className="ml-auto text-zinc-400 tabular-nums flex-shrink-0">{item.time}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function PhoneMockup() {
  return (
    <div className="relative" style={{ width: 280 }}>
      <div className="relative rounded-[44px] p-[3px]" style={{ background: "linear-gradient(145deg, #3a3a3c 0%, #1c1c1e 50%, #3a3a3c 100%)", filter: "drop-shadow(0 32px 64px rgba(0,0,0,0.45))" }}>
        <div className="absolute -left-[3px] top-[88px] w-[3px] h-8 rounded-l-full bg-zinc-700" />
        <div className="absolute -left-[3px] top-[132px] w-[3px] h-12 rounded-l-full bg-zinc-700" />
        <div className="absolute -left-[3px] top-[156px] w-[3px] h-12 rounded-l-full bg-zinc-700" />
        <div className="absolute -right-[3px] top-[120px] w-[3px] h-16 rounded-r-full bg-zinc-700" />
        <div className="rounded-[42px] overflow-hidden bg-black">
          <div className="relative bg-black flex justify-center pt-3 pb-1 px-6">
            <div className="w-24 h-6 rounded-full bg-black border border-zinc-800 flex items-center justify-center gap-2">
              <div className="w-2 h-2 rounded-full bg-zinc-700" />
              <div className="w-3 h-3 rounded-full bg-zinc-700" />
            </div>
          </div>
          <div className="relative" style={{ aspectRatio: "9/18" }}>
            <video src="https://gyms.fra1.digitaloceanspaces.com/website/flex/flexaccess.mp4" autoPlay muted loop playsInline className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />
          </div>
          <div className="bg-black flex justify-center py-2">
            <div className="w-24 h-1 rounded-full bg-zinc-600" />
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

export function MainLandingPage() {
  const [selectedModules, setSelectedModules] = useState<Set<ModuleKey>>(new Set(["business"]))
  const [extraLocations, setExtraLocations] = useState(0)
  const [lang, setLang] = useState<Lang>("en")
  const [eurOrUsd, setEurOrUsd] = useState<"eur" | "usd">("eur")
  const [activeShowcaseTab, setActiveShowcaseTab] = useState(0)
  const [showcasePaused, setShowcasePaused] = useState(false)

  useEffect(() => {
    if (showcasePaused) return
    const timer = setInterval(() => {
      setActiveShowcaseTab(prev => (prev + 1) % 3)
    }, 5000)
    return () => clearInterval(timer)
  }, [showcasePaused])

  // Albanian forces ALL (Lek). EN/IT respect the EUR/USD toggle.
  const currency: "eur" | "usd" | "all" = lang === "sq" ? "all" : eurOrUsd

  // Format a EUR-base price in the active currency.
  // ALL: round to nearest 1,000 (€49 → 5,000 ALL · €499 → 50,000 ALL).
  const fmt = (eur: number): string => {
    if (currency === "all") {
      const all = Math.round((eur * 100) / 1000) * 1000
      return `${all.toLocaleString("en-US")} ALL`
    }
    const sym = currency === "eur" ? "€" : "$"
    return `${sym}${eur}`
  }

  const { resolvedTheme } = useTheme()
  const isDark = resolvedTheme === "dark"
  const t = translations[lang]

  const toggleModule = (id: ModuleKey) => {
    setSelectedModules(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectAll = () => setSelectedModules(new Set(MODULES_CONFIG.map(m => m.id)))

  const modulesTotal = MODULES_CONFIG.filter(m => selectedModules.has(m.id)).reduce((sum, m) => sum + m.price, 0)
  const locationsTotal = extraLocations * LOCATION_PRICE
  const grandTotal = modulesTotal + locationsTotal

  const showcaseTabs = [
    { label: t.showcase.tabs.diet,    desc: t.showcase.dietDesc },
    { label: t.showcase.tabs.workout, desc: t.showcase.workoutDesc },
    { label: t.showcase.tabs.promo,   desc: t.showcase.promoDesc, soon: true },
  ]

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <SEOHead
        title="THE NOT FIT — AI-Powered Fitness Management Platform"
        description="AI-powered fitness management platform for modern gym chains and franchises. Membership management, scheduling, nutrition plans, analytics, and more."
      />

      <AnnouncementBar
        eyebrow={t.announcement.eyebrow}
        message={t.announcement.message}
        cta={t.announcement.cta}
      />

      {/* Navigation */}
      <nav className="border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1.5">
            <span className="text-lg font-semibold tracking-tight">THE</span>
            <div className="relative w-10 h-10">
              <img src="/logo.png" alt="THE NOT FIT" className="w-full h-full object-contain" loading="eager" />
            </div>
            <span className="text-lg font-semibold tracking-tight">FIT</span>
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm text-muted-foreground">
            <a href="#features"  className="hover:text-foreground transition-colors">{t.nav.features}</a>
            <a href="#showcase"  className="hover:text-foreground transition-colors">{t.nav.showcase}</a>
            <a href="#pricing"   className="hover:text-foreground transition-colors">{t.nav.pricing}</a>
            <a href="#customers" className="hover:text-foreground transition-colors">{t.nav.customers}</a>
          </div>
          <div className="flex items-center gap-2">
            <LanguagePicker lang={lang} setLang={setLang} />
            <Button variant="ghost" asChild className="hidden sm:inline-flex">
              <Link to="/login">{t.nav.signIn}</Link>
            </Button>
            <Button asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <Link to="/signup">{t.nav.getStarted}</Link>
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative">
        <HeroMesh />
        <SectionDecor variant="a" />
        <div className="container mx-auto px-4 pt-20 md:pt-28 pb-16 md:pb-24 relative">
          <div className="grid lg:grid-cols-[1.05fr_1fr] gap-12 lg:gap-16 items-center max-w-7xl mx-auto">
            <Reveal>
            <div className="space-y-7">
              <div className="inline-flex items-center gap-2 rounded-full border bg-background/60 backdrop-blur px-3 py-1 text-xs font-medium text-muted-foreground">
                <BrandMark size={9} />
                <span>{t.hero.badge}</span>
              </div>
              <h1 className="text-balance text-5xl md:text-6xl font-semibold tracking-[-0.02em] leading-[1.05]">
                {t.hero.heading1}{" "}
                <span className="bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent">
                  {t.hero.heading2}
                </span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
                {t.hero.description}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-1">
                <Button size="lg" asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                  <Link to="/signup" className="inline-flex items-center gap-2">
                    {t.hero.cta1}
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link to="/support">{t.hero.cta2}</Link>
                </Button>
              </div>
              <a
                href="#offer"
                className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-3 py-1.5 text-xs font-medium text-primary hover:bg-primary/10 transition-colors group"
              >
                <Clock className="w-3.5 h-3.5" strokeWidth={2.5} />
                <span>{t.hero.offerPill}</span>
                <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
              </a>
              <p className="text-xs text-muted-foreground">{t.hero.footnote}</p>
            </div>
            </Reveal>

            <Reveal delay={150} className="relative hidden lg:block">
              <BrowserChrome accent="primary">
                <AdminDashboardMock />
              </BrowserChrome>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="relative border-b">
        <SectionDecor variant="d" />
        <div className="container mx-auto px-4 py-14 relative">
          <Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-y-8 max-w-5xl mx-auto md:divide-x md:divide-border">
            {[
              { Icon: Building2,    value: "10+",     label: t.stats.gyms,           color: BRAND_RED },
              { Icon: Users,        value: "10,000+", label: t.stats.activeMembers,  color: BRAND_GREEN },
              { Icon: ShieldCheck,  value: "99.9%",   label: t.stats.uptimeSla,      color: BRAND_BLUE },
              { Icon: Headphones,   value: "24/7",    label: t.stats.support,        color: BRAND_RED },
            ].map(({ Icon, value, label, color }) => (
              <div key={label} className="flex flex-col items-center text-center px-4">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: `${color}15` }}>
                  <Icon className="w-4 h-4" style={{ color }} strokeWidth={1.75} />
                </div>
                <div className="text-3xl md:text-4xl font-semibold tracking-tight tabular-nums" style={{ color }}>{value}</div>
                <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-medium mt-2">{label}</div>
              </div>
            ))}
          </div>
          </Reveal>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="relative py-24 md:py-28">
        <SectionDecor variant="b" />
        <div className="container mx-auto px-4 relative">
          <Reveal>
          <div className="relative max-w-5xl mx-auto rounded-3xl overflow-hidden border shadow-xl aspect-[21/9] mb-14">
            <img
              src="https://gyms.fra1.digitaloceanspaces.com/website/flex/flexgym.jpg"
              alt="Member training at FlexGym Tirana"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div aria-hidden className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/15 to-transparent" />
            <div className="absolute bottom-5 left-6 right-6 flex items-end justify-between text-white">
              <div className="space-y-1">
                <div className="flex gap-1">
                  <span className="w-2.5 h-2.5" style={{ backgroundColor: BRAND_RED }} />
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: BRAND_GREEN }} />
                  <svg width="10" height="10" viewBox="0 0 12 12"><polygon points="0,0 12,0 6,12" fill={BRAND_BLUE} /></svg>
                </div>
                <p className="text-sm font-medium">FlexGym Tirana — running on THE NOT FIT</p>
              </div>
            </div>
          </div>
          </Reveal>

          <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4">{t.features.eyebrow}</p>
            <h2 className="text-balance text-3xl md:text-4xl font-semibold tracking-[-0.02em] mb-4">{t.features.heading}</h2>
            <p className="text-lg text-muted-foreground">{t.features.subheading}</p>
            <p className="text-sm text-primary font-medium mt-4">{t.features.valueProp}</p>
          </div>
          </Reveal>

          <Reveal delay={120}>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {t.features.cards.map((card, i) => {
              const Icon = FEATURE_ICONS[i]
              const color = BRAND_COLORS[i % 3]
              return (
                <Card key={i} className="rounded-2xl border bg-card transition-colors hover:border-foreground/20 group">
                  <CardContent className="p-6 space-y-4">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center transition-transform group-hover:scale-105" style={{ backgroundColor: `${color}15` }}>
                      <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.75} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base">{card.title}</h3>
                      <p className="text-sm text-muted-foreground mt-0.5">{card.desc_short}</p>
                    </div>
                    <p className="text-sm text-muted-foreground leading-relaxed">{card.desc}</p>
                  </CardContent>
                </Card>
              )
            })}
          </div>
          </Reveal>
        </div>
      </section>

      {/* AI Showcase */}
      <section id="showcase" className="relative py-24 md:py-28 bg-secondary/30 border-y">
        <SectionDecor variant="c" />
        <div className="container mx-auto px-4 relative">
          <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4">{t.showcase.eyebrow}</p>
            <h2 className="text-balance text-3xl md:text-4xl font-semibold tracking-[-0.02em] mb-4">{t.showcase.heading}</h2>
            <p className="text-lg text-muted-foreground">{t.showcase.subheading}</p>
          </div>
          </Reveal>

          <Reveal delay={120}>
          <div
            className="max-w-5xl mx-auto"
            onMouseEnter={() => setShowcasePaused(true)}
            onMouseLeave={() => setShowcasePaused(false)}
          >
            <div className="flex justify-center gap-2 mb-6 flex-wrap">
              {showcaseTabs.map((tab, idx) => {
                const isActive = activeShowcaseTab === idx
                return (
                  <button
                    key={tab.label}
                    onClick={() => setActiveShowcaseTab(idx)}
                    className={`relative px-4 py-2 rounded-full text-sm font-medium transition-all border ${
                      isActive
                        ? "bg-foreground text-background border-foreground"
                        : "text-muted-foreground hover:text-foreground bg-background border-border"
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {tab.label}
                      {tab.soon && (
                        <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full tracking-wider ${isActive ? "bg-background/20" : "bg-secondary"}`}>
                          {t.showcase.comingSoon}
                        </span>
                      )}
                    </span>
                  </button>
                )
              })}
            </div>

            <p className="text-center text-sm text-muted-foreground mb-6 max-w-2xl mx-auto min-h-[2.5rem]">
              {showcaseTabs[activeShowcaseTab].desc}
            </p>

            <div className="relative">
              <BrowserChrome accent="primary">
                <div className="relative">
                  <div className={`transition-opacity duration-500 ${activeShowcaseTab === 0 ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`}>
                    <DietPlannerMock themeIdx={0} />
                  </div>
                  <div className={`transition-opacity duration-500 ${activeShowcaseTab === 1 ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`}>
                    <WorkoutMock themeIdx={1} />
                  </div>
                  <div className={`transition-opacity duration-500 ${activeShowcaseTab === 2 ? "opacity-100" : "opacity-0 absolute inset-0 pointer-events-none"}`}>
                    <PromoMock comingSoonLabel={t.showcase.comingSoon} />
                  </div>
                </div>
              </BrowserChrome>
            </div>

            <div className="flex justify-center gap-2 mt-6">
              {[0, 1, 2].map(i => (
                <button
                  key={i}
                  onClick={() => setActiveShowcaseTab(i)}
                  aria-label={`Show ${showcaseTabs[i].label}`}
                  className="h-1.5 rounded-full transition-all duration-300"
                  style={{
                    width: activeShowcaseTab === i ? 24 : 6,
                    backgroundColor: activeShowcaseTab === i
                      ? "hsl(var(--primary))"
                      : isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)",
                  }}
                />
              ))}
            </div>
          </div>
          </Reveal>
        </div>
      </section>

      {/* Mobile callout */}
      <section className="relative py-24 md:py-28">
        <SectionDecor variant="f" />
        <div className="container mx-auto px-4 relative">
          <Reveal>
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-6xl mx-auto">
            <div className="flex justify-center lg:justify-start">
              <PhoneMockup />
            </div>
            <div className="space-y-6">
              <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">{t.mobile.eyebrow}</p>
              <h2 className="text-balance text-3xl md:text-4xl font-semibold tracking-[-0.02em]">{t.mobile.heading}</h2>
              <p className="text-lg text-muted-foreground">{t.mobile.sub}</p>
              <ul className="space-y-3">
                {[t.mobile.bullet1, t.mobile.bullet2, t.mobile.bullet3].map(b => (
                  <li key={b} className="flex items-start gap-3 text-sm">
                    <div className="mt-0.5 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-primary" strokeWidth={3} />
                    </div>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <div className="pt-2 flex items-center gap-3">
                <Smartphone className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <span className="text-xs text-muted-foreground">iOS · Android · Web</span>
              </div>
            </div>
          </div>
          </Reveal>
        </div>
      </section>

      {/* Benefits — Why THE [brand-mark] FIT? */}
      <section className="relative py-24 md:py-28 bg-secondary/30 border-y">
        <SectionDecor variant="b" />
        <div className="container mx-auto px-4 relative">
          <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-14">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4">{t.benefits.eyebrow}</p>
            <h2 className="text-balance text-3xl md:text-4xl font-semibold tracking-[-0.02em] mb-4 inline-flex items-center justify-center flex-wrap gap-x-2">
              {(() => {
                const parts = t.benefits.heading.split("NOT")
                if (parts.length !== 2) return t.benefits.heading
                return (
                  <>
                    <span>{parts[0].trim()}</span>
                    <span className="inline-flex items-center align-baseline gap-[3px]" aria-hidden>
                      <span className="inline-block w-[0.6em] h-[0.6em]" style={{ backgroundColor: BRAND_RED }} />
                      <span className="inline-block w-[0.6em] h-[0.6em] rounded-full" style={{ backgroundColor: BRAND_GREEN }} />
                      <svg className="inline-block w-[0.6em] h-[0.6em]" viewBox="0 0 40 40">
                        <polygon points="2,2 38,2 20,38" fill={BRAND_BLUE} />
                      </svg>
                    </span>
                    <span>{parts[1].trim()}</span>
                  </>
                )
              })()}
            </h2>
            <p className="text-lg text-muted-foreground">{t.benefits.subheading}</p>
          </div>
          </Reveal>

          <Reveal delay={120}>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-10 max-w-6xl mx-auto">
            {t.benefits.items.map((item, i) => {
              const Icon = [Salad, Dumbbell, ShieldCheck, BarChart3, Bot, Building2][i]
              const color = BRAND_COLORS[i % 3]
              return (
                <div key={i} className="space-y-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                    <Icon className="w-5 h-5" style={{ color }} strokeWidth={1.75} />
                  </div>
                  <h3 className="font-semibold text-base">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              )
            })}
          </div>
          </Reveal>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="relative py-24 md:py-28 bg-secondary/30 border-y">
        <SectionDecor variant="e" />
        <div className="container mx-auto px-4 relative">
          <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4">{t.pricing.eyebrow}</p>
            <h2 className="text-balance text-3xl md:text-4xl font-semibold tracking-[-0.02em] mb-4">{t.pricing.heading}</h2>
            <p className="text-lg text-muted-foreground">{t.pricing.subheading}</p>
            {lang !== "sq" && (
              <div className="flex justify-center pt-6">
                <div className="inline-flex rounded-full border p-1 bg-background">
                  {(["eur", "usd"] as const).map(c => (
                    <button
                      key={c}
                      onClick={() => setEurOrUsd(c)}
                      className={`px-4 py-1.5 rounded-full text-xs font-semibold tracking-wider uppercase transition-colors ${
                        currency === c ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {c === "eur" ? "EUR €" : "USD $"}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 max-w-7xl mx-auto mb-12">
            {MODULES_CONFIG.map(m => {
              const Icon = m.icon
              const mod = t.pricing.modules[m.id]
              return (
                <Card key={m.id} className="relative rounded-2xl border bg-card transition-colors hover:border-foreground/20">
                  {m.comingSoon && (
                    <span className="absolute -top-2.5 right-4 text-[10px] font-bold tracking-wider px-2 py-0.5 rounded-full bg-foreground text-background">
                      {t.pricing.comingSoon}
                    </span>
                  )}
                  <CardContent className="p-5 space-y-4">
                    <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center">
                      <Icon className="w-4.5 h-4.5 text-foreground" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base">{mod.name}</h3>
                      <div className="flex items-baseline gap-1 mt-1">
                        <span className="text-2xl font-semibold tracking-tight">{fmt(m.price)}</span>
                        <span className="text-xs text-muted-foreground">{t.pricing.perMonth}</span>
                      </div>
                    </div>
                    <ul className="space-y-1.5 text-sm text-muted-foreground">
                      {mod.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-3.5 h-3.5 mt-0.5 text-primary flex-shrink-0" strokeWidth={2.5} />
                          <span>{f}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              )
            })}

            {/* Extra Locations */}
            <Card className="rounded-2xl border bg-card transition-colors hover:border-foreground/20">
              <CardContent className="p-5 space-y-4">
                <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center">
                  <MapPin className="w-4.5 h-4.5 text-foreground" strokeWidth={1.5} />
                </div>
                <div>
                  <h3 className="font-semibold text-base">{t.pricing.extraLocations.name}</h3>
                  <div className="flex items-baseline gap-1 mt-1">
                    <span className="text-2xl font-semibold tracking-tight">{fmt(LOCATION_PRICE)}</span>
                    <span className="text-xs text-muted-foreground">{t.pricing.perMonthEach}</span>
                  </div>
                </div>
                <ul className="space-y-1.5 text-sm text-muted-foreground">
                  {t.pricing.extraLocations.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 mt-0.5 text-primary flex-shrink-0" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          {/* Limited-time offer band — tri-color gradient mesh */}
          <div id="offer" className="max-w-5xl mx-auto mb-12 scroll-mt-24">
            <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-zinc-950 text-white shadow-2xl">
              {/* Tri-color gradient mesh */}
              <div aria-hidden className="absolute inset-0">
                <div className="absolute -top-32 -left-20 h-[460px] w-[460px] rounded-full blur-[110px]" style={{ backgroundColor: BRAND_RED, opacity: 0.55 }} />
                <div className="absolute top-1/4 left-1/3 h-[400px] w-[400px] rounded-full blur-[120px]" style={{ backgroundColor: BRAND_GREEN, opacity: 0.45 }} />
                <div className="absolute -bottom-40 -right-20 h-[500px] w-[500px] rounded-full blur-[110px]" style={{ backgroundColor: BRAND_BLUE, opacity: 0.55 }} />
              </div>

              {/* Oversized decorative shapes */}
              <div aria-hidden className="absolute inset-0 pointer-events-none overflow-hidden">
                <div className="absolute -top-4 right-16 w-32 h-32 rotate-12 mix-blend-screen" style={{ backgroundColor: BRAND_RED, opacity: 0.18 }} />
                <div className="absolute bottom-6 left-12 w-40 h-40 rounded-full mix-blend-screen" style={{ backgroundColor: BRAND_GREEN, opacity: 0.16 }} />
                <svg className="absolute top-12 left-1/2 -translate-x-1/2 w-28 h-28 mix-blend-screen" viewBox="0 0 40 40" style={{ opacity: 0.18 }}>
                  <polygon points="2,2 38,2 20,38" fill={BRAND_BLUE} />
                </svg>
                <div className="absolute top-1/3 right-1/3 w-3 h-3 rounded-full" style={{ backgroundColor: BRAND_RED, opacity: 0.6 }} />
                <div className="absolute bottom-1/3 right-12 w-2.5 h-2.5" style={{ backgroundColor: BRAND_GREEN, opacity: 0.6 }} />
                <svg className="absolute top-1/2 left-8 w-3 h-3" viewBox="0 0 12 12" style={{ opacity: 0.6 }}>
                  <polygon points="0,0 12,0 6,12" fill={BRAND_BLUE} />
                </svg>
                {/* Subtle grid */}
                <div
                  className="absolute inset-0 opacity-[0.06]"
                  style={{
                    backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)",
                    backgroundSize: "24px 24px",
                  }}
                />
              </div>

              {/* Top tri-color hairline */}
              <div
                aria-hidden
                className="absolute top-0 left-0 right-0 h-[3px]"
                style={{ background: `linear-gradient(90deg, ${BRAND_RED} 0%, ${BRAND_GREEN} 50%, ${BRAND_BLUE} 100%)` }}
              />

              <div className="relative p-8 md:p-12 grid md:grid-cols-[1.3fr_1fr] gap-10 items-center">
                {/* Left: copy */}
                <div className="space-y-5">
                  <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/15 px-3 py-1 text-[10px] font-bold tracking-[0.2em] uppercase">
                    <BrandMark size={9} />
                    <Clock className="w-3 h-3" strokeWidth={2.5} />
                    <span>{t.pricing.offer.eyebrow}</span>
                  </div>
                  <h3 className="text-3xl md:text-4xl font-semibold tracking-[-0.02em] leading-[1.1]">
                    {t.pricing.offer.heading}
                  </h3>
                  <p className="text-sm md:text-base text-white/75 max-w-md leading-relaxed">
                    {t.pricing.offer.subheading}
                  </p>
                  <div className="flex items-center gap-1.5 text-xs text-white/70">
                    <Sparkles className="w-3.5 h-3.5" style={{ color: BRAND_GREEN }} strokeWidth={2} />
                    <span>{t.pricing.offer.compare}</span>
                  </div>
                </div>

                {/* Right: price + CTA */}
                <div className="flex flex-col items-start md:items-end gap-4">
                  <div className="relative inline-block">
                    {/* Glowing tri-color halo behind price */}
                    <span
                      aria-hidden
                      className="absolute -inset-6 rounded-full blur-2xl opacity-70"
                      style={{
                        background: `conic-gradient(from 0deg, ${BRAND_RED}, ${BRAND_GREEN}, ${BRAND_BLUE}, ${BRAND_RED})`,
                      }}
                    />
                    <span className="relative flex items-baseline gap-1">
                      <span className="text-7xl md:text-[5.5rem] font-bold tracking-[-0.03em] tabular-nums leading-none">{fmt(499)}</span>
                    </span>
                  </div>
                  <span className="text-sm text-white/80 font-medium tracking-wide">{t.pricing.offer.unit}</span>
                  <Button asChild size="lg" className="bg-white text-zinc-950 hover:bg-white/90 shadow-lg shadow-black/20 mt-1">
                    <Link to="/signup" className="inline-flex items-center gap-2">
                      {t.pricing.offer.cta}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Bottom tri-color hairline */}
              <div
                aria-hidden
                className="absolute bottom-0 left-0 right-0 h-[3px]"
                style={{ background: `linear-gradient(90deg, ${BRAND_BLUE} 0%, ${BRAND_GREEN} 50%, ${BRAND_RED} 100%)` }}
              />
            </div>
          </div>

          {/* Pricing Calculator */}
          <Card className="max-w-2xl mx-auto rounded-2xl border bg-background/80 backdrop-blur">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-base font-semibold">{t.pricing.calculator.title}</h3>
                <button
                  onClick={selectAll}
                  className="text-xs font-medium px-3 py-1.5 rounded-full border hover:bg-secondary transition-colors"
                >
                  {t.pricing.calculator.selectAll}
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
                {MODULES_CONFIG.map(m => {
                  const checked = selectedModules.has(m.id)
                  return (
                    <label
                      key={m.id}
                      className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                        checked ? "border-primary/40 bg-primary/5" : "border-border hover:border-foreground/20"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleModule(m.id)}
                        className="sr-only"
                      />
                      <div
                        className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center transition-colors ${
                          checked ? "border-primary bg-primary" : "border-muted-foreground/30"
                        }`}
                      >
                        {checked && <Check className="w-2.5 h-2.5 text-primary-foreground" strokeWidth={3} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium truncate">{t.pricing.modules[m.id].name}</span>
                          {m.comingSoon && (
                            <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground flex-shrink-0">
                              {t.pricing.soon}
                            </span>
                          )}
                        </div>
                      </div>
                      <span className="text-sm font-semibold flex-shrink-0 tabular-nums">{fmt(m.price)}</span>
                    </label>
                  )
                })}
              </div>

              <div className="mb-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">{t.pricing.calculator.extraLocations}</span>
                  <span className="text-xs text-muted-foreground">{fmt(LOCATION_PRICE)}{t.pricing.perMonthEach}</span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setExtraLocations(Math.max(0, extraLocations - 1))}
                    className="w-9 h-9 rounded-lg border flex items-center justify-center hover:bg-secondary transition-colors"
                    aria-label="Decrease"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <div className="flex-1 bg-secondary/50 rounded-lg h-9 flex items-center justify-center">
                    <span className="text-sm font-semibold tabular-nums">{extraLocations}</span>
                  </div>
                  <button
                    onClick={() => setExtraLocations(extraLocations + 1)}
                    className="w-9 h-9 rounded-lg border flex items-center justify-center hover:bg-secondary transition-colors"
                    aria-label="Increase"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="border-t pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{t.pricing.calculator.modules(selectedModules.size)}</span>
                  <span className="font-medium tabular-nums">{fmt(modulesTotal)}{t.pricing.perMonth}</span>
                </div>
                {extraLocations > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">{t.pricing.calculator.extraLocs(extraLocations)}</span>
                    <span className="font-medium tabular-nums">{fmt(locationsTotal)}{t.pricing.perMonth}</span>
                  </div>
                )}
                <div className="flex justify-between items-baseline pt-3 border-t">
                  <span className="font-semibold">{t.pricing.calculator.total}</span>
                  <span className="text-2xl font-semibold tabular-nums text-primary">
                    {fmt(grandTotal)}<span className="text-xs font-medium text-muted-foreground ml-0.5">{t.pricing.perMonth}</span>
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="text-center mt-12 space-y-4">
            <p className="text-sm text-muted-foreground">{t.pricing.cta.sub}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/signup" className="inline-flex items-center gap-2">
                  {t.pricing.cta.btn1}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link to="/support">{t.pricing.cta.btn2}</Link>
              </Button>
            </div>
          </div>
          </Reveal>
        </div>
      </section>

      {/* Case Study */}
      <section id="customers" className="relative py-24 md:py-28">
        <SectionDecor variant="a" />
        <div className="container mx-auto px-4 relative">
          <Reveal>
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-14">
              <div className="inline-flex items-center gap-2 mb-4">
                <BrandMark size={8} />
                <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">{t.caseStudy.eyebrow}</p>
              </div>
              <h2 className="text-balance text-3xl md:text-4xl font-semibold tracking-[-0.02em] mb-4">
                {t.caseStudy.heading1}{" "}
                <span className="bg-gradient-to-r from-foreground to-primary bg-clip-text text-transparent">{t.caseStudy.heading2}</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">{t.caseStudy.description}</p>
            </div>

            <div className="grid md:grid-cols-3 gap-5 mb-12">
              {GYMS.map((gym, i) => {
                const info = t.caseStudy.gyms[i]
                return (
                  <Card key={gym.name} className="group rounded-2xl overflow-hidden border bg-card transition-colors hover:border-foreground/20">
                    <div className="relative h-48 overflow-hidden">
                      <img src={gym.image} alt={gym.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute bottom-3 left-4 right-4">
                        <h3 className="text-white font-semibold text-base">{gym.name}</h3>
                        <p className="text-white/80 text-xs">{info.subtitle}</p>
                      </div>
                    </div>
                    <CardContent className="p-5 space-y-4">
                      <div className="flex items-baseline gap-2">
                        <span className="text-xl font-semibold tracking-tight tabular-nums">{info.members}</span>
                        <span className="text-xs text-muted-foreground">{info.highlight}</span>
                      </div>
                      <ul className="space-y-1.5">
                        {info.features.map((f, j) => (
                          <li key={j} className="flex items-center gap-2 text-sm text-muted-foreground">
                            <span className="w-1 h-1 rounded-full bg-foreground/40 flex-shrink-0" />
                            {f}
                          </li>
                        ))}
                      </ul>
                      <a
                        href={gym.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground hover:text-primary transition-colors"
                      >
                        <Instagram className="w-3.5 h-3.5" strokeWidth={1.5} />
                        {t.caseStudy.followInstagram}
                      </a>
                    </CardContent>
                  </Card>
                )
              })}
            </div>

            <Card className="rounded-2xl border bg-card">
              <CardContent className="p-8 md:p-10 grid md:grid-cols-2 gap-8 items-center">
                <div className="space-y-4">
                  <div className="flex gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <blockquote className="text-base md:text-lg leading-relaxed text-foreground">
                    “{t.caseStudy.testimonial}”
                  </blockquote>
                  <div className="flex items-center gap-3 pt-2">
                    <div className="flex-shrink-0 rounded-md overflow-hidden bg-black p-1.5">
                      <img src={GYMS[0].logo} alt="Flex Gym Group" className="h-7 w-auto object-contain" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm">Flex Gym Group</div>
                      <div className="text-muted-foreground text-xs">{t.caseStudy.operator}</div>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { value: "3",       color: BRAND_RED },
                    { value: "3,000+",  color: BRAND_GREEN },
                    { value: "50+",     color: BRAND_BLUE },
                    { value: "1",       color: BRAND_RED },
                  ].map((stat, i) => (
                    <div key={i} className="text-center p-4 rounded-xl border bg-secondary/30">
                      <div className="text-2xl font-semibold tracking-tight tabular-nums" style={{ color: stat.color }}>{stat.value}</div>
                      <div className="text-xs uppercase tracking-[0.14em] text-muted-foreground font-medium mt-2">{t.caseStudy.statsLabels[i]}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 md:py-28 bg-foreground text-background overflow-hidden">
        <CtaMesh />
        <div className="container mx-auto px-4 text-center relative">
          <Reveal>
          <div className="max-w-3xl mx-auto space-y-6">
            <h2 className="text-balance text-3xl md:text-5xl font-semibold tracking-[-0.02em] leading-[1.1]">
              {t.cta.heading1}{" "}
              <span className="bg-gradient-to-r from-background to-primary bg-clip-text text-transparent">
                {t.cta.heading2}
              </span>
            </h2>
            <p className="text-base md:text-lg text-background/70 leading-relaxed">
              {t.cta.description.split("\n").map((line, i) => (
                <span key={i}>{line}{i === 0 && <br />}</span>
              ))}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <Button size="lg" asChild className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/signup" className="inline-flex items-center gap-2">
                  {t.cta.btn1}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild className="bg-transparent border-background/30 text-background hover:bg-background/10 hover:text-background">
                <Link to="/support">{t.cta.btn2}</Link>
              </Button>
            </div>
            <div className="flex items-center justify-center gap-6 pt-4 text-xs text-background/60 flex-wrap">
              {[t.cta.check1, t.cta.check2, t.cta.check3].map((c, i) => (
                <div key={c} className="flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5" strokeWidth={3} style={{ color: BRAND_COLORS[i] }} />
                  <span>{c}</span>
                </div>
              ))}
            </div>
          </div>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t bg-background">
        <div className="container mx-auto px-4 py-14">
          <div className="grid md:grid-cols-[1.5fr_1fr_1fr] gap-10 mb-10">
            <div className="space-y-4 max-w-sm">
              <Link to="/" className="flex items-center gap-1.5">
                <span className="text-base font-semibold tracking-tight">THE</span>
                <div className="relative w-9 h-9">
                  <img src="/logo.png" alt="THE NOT FIT" className="w-full h-full object-contain" />
                </div>
                <span className="text-base font-semibold tracking-tight">FIT</span>
              </Link>
              <p className="text-sm text-muted-foreground">{t.footer.tagline}</p>
              <div className="flex items-center gap-2 pt-1">
                <a
                  href="https://www.instagram.com/thenotfitapp/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Follow THE NOT FIT on Instagram"
                  className="w-9 h-9 rounded-lg border flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-foreground/20 transition-colors"
                >
                  <Instagram className="w-4 h-4" strokeWidth={1.75} />
                </a>
              </div>
            </div>

            <div>
              <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4">{t.footer.productHeading}</h4>
              <ul className="space-y-2 text-sm">
                {t.footer.productLinks.map(link => (
                  <li key={link.label}>
                    <a href={link.href} className="text-foreground/80 hover:text-foreground transition-colors">{link.label}</a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground mb-4">{t.footer.legalHeading}</h4>
              <ul className="space-y-2 text-sm">
                {t.footer.legalLinks.map(link => (
                  <li key={link.label}>
                    <Link to={link.href} className="text-foreground/80 hover:text-foreground transition-colors">{link.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="border-t pt-6 flex flex-col md:flex-row justify-between items-center gap-4 text-xs text-muted-foreground">
            <p>{t.footer.copyright}</p>
            <LanguagePicker lang={lang} setLang={setLang} upward />
          </div>
        </div>
        <GrantFooterBand lang={lang} />
      </footer>
    </div>
  )
}
